import { useEffect, useRef } from 'react';
import {
  type Control,
  type UseFormGetValues,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import {
  QUIZ_GROUP_MIN_TERM,
  QUIZ_GROUP_MIN_USERS,
} from '../constants/quizQuote.const';
import { type QuizQuoteFormValues } from '../utils/quizQuote.schema';

interface UseQuotePlanTypeUsageMemoryParams {
  control: Control<QuizQuoteFormValues>;
  getValues: UseFormGetValues<QuizQuoteFormValues>;
  setValue: UseFormSetValue<QuizQuoteFormValues>;
}

interface QuoteUsageValues {
  term: number;
  userCount: number;
}

function toGroupUsage(values: QuoteUsageValues): QuoteUsageValues {
  return {
    term: Math.max(values.term, QUIZ_GROUP_MIN_TERM),
    userCount: Math.max(values.userCount, QUIZ_GROUP_MIN_USERS),
  };
}

function setUsageValues(
  setValue: UseFormSetValue<QuizQuoteFormValues>,
  values: QuoteUsageValues,
) {
  setValue('term', values.term, {
    shouldDirty: true,
    shouldValidate: true,
  });
  setValue('user_count', values.userCount, {
    shouldDirty: true,
    shouldValidate: true,
  });
}

export function useQuotePlanTypeUsageMemory({
  control,
  getValues,
  setValue,
}: UseQuotePlanTypeUsageMemoryParams) {
  const planType = useWatch<QuizQuoteFormValues, 'plan_type'>({
    control,
    name: 'plan_type',
  });
  const plan = useWatch<QuizQuoteFormValues, 'plan'>({
    control,
    name: 'plan',
  });
  const term = useWatch<QuizQuoteFormValues, 'term'>({ control, name: 'term' });
  const userCount = useWatch<QuizQuoteFormValues, 'user_count'>({
    control,
    name: 'user_count',
  });

  const previousPlanRef = useRef<QuizQuoteFormValues['plan']>(plan);
  const previousPlanTypeRef =
    useRef<QuizQuoteFormValues['plan_type']>(planType);
  const lastProPlanTypeRef = useRef<QuizQuoteFormValues['plan_type']>(planType);
  const lastIndividualUsageRef = useRef<QuoteUsageValues | null>(
    planType === 'individual' ? { term, userCount } : null,
  );

  useEffect(() => {
    const previousPlan = previousPlanRef.current;
    const previousPlanType = previousPlanTypeRef.current;
    const currentValues = {
      term: getValues('term'),
      userCount: getValues('user_count'),
    };

    if (previousPlan !== plan) {
      if (previousPlan === 'pro') {
        lastProPlanTypeRef.current = previousPlanType;
      }

      if (plan === 'basic') {
        if (previousPlanType === 'individual') {
          lastIndividualUsageRef.current = currentValues;
        }

        if (planType !== 'individual') {
          setValue('plan_type', 'individual', {
            shouldDirty: true,
            shouldValidate: true,
          });
        }

        previousPlanRef.current = plan;
        previousPlanTypeRef.current = 'individual';
        return;
      }

      const restoredPlanType = lastProPlanTypeRef.current;
      if (planType !== restoredPlanType) {
        setValue('plan_type', restoredPlanType, {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else if (restoredPlanType === 'group') {
        setUsageValues(setValue, toGroupUsage(currentValues));
      } else if (lastIndividualUsageRef.current) {
        setUsageValues(setValue, lastIndividualUsageRef.current);
      }

      previousPlanRef.current = plan;
      previousPlanTypeRef.current = restoredPlanType;
      return;
    }

    if (previousPlanType !== planType) {
      if (plan === 'pro' && previousPlanType === 'individual') {
        lastIndividualUsageRef.current = currentValues;
      }

      if (planType === 'group') {
        setUsageValues(setValue, toGroupUsage(currentValues));
      } else if (lastIndividualUsageRef.current) {
        setUsageValues(setValue, lastIndividualUsageRef.current);
      }

      previousPlanTypeRef.current = planType;
      if (plan === 'pro') {
        lastProPlanTypeRef.current = planType;
      }
      return;
    }

    if (plan === 'pro' && planType === 'individual') {
      lastIndividualUsageRef.current = { term, userCount };
      lastProPlanTypeRef.current = planType;
      return;
    }

    if (plan === 'pro') {
      lastProPlanTypeRef.current = planType;
    }

    if (planType !== 'group') return;

    const groupUsage = toGroupUsage({ term, userCount });
    if (groupUsage.term !== term || groupUsage.userCount !== userCount) {
      setUsageValues(setValue, groupUsage);
    }
  }, [getValues, plan, planType, setValue, term, userCount]);
}
